/**
 * The physics a `<schmancy-time-picker>` wheel moves under — arithmetic only,
 * no DOM, so the half of this primitive that cannot be read off a screenshot
 * can be read, reasoned about and executed on its own. Its sibling
 * `time-picker.slots.ts` does the same for the slot rule.
 *
 * Every quantity is in CSS pixels and SECONDS. Timestamps arrive in
 * milliseconds from `Event.timeStamp` and `requestAnimationFrame`, and the
 * component divides once at that boundary — a module that mixed the two units
 * would make every constant below ambiguous.
 */

/** One position reading of a wheel, taken as the finger moves. */
export interface Sample {
	/** Seconds, on the same clock every other sample was taken against. */
	readonly at: number
	/** The wheel's applied offset at that instant. */
	readonly y: number
}

/** The trailing window a release velocity is fitted over. Long enough that a
 *  120Hz frame stream contributes a dozen readings rather than two — a
 *  last-two-points delta at that rate is one frame of pointer jitter — and
 *  short enough that a finger which slowed to a stop before lifting reads as
 *  stopped. */
export const VELOCITY_WINDOW = 0.1

/** The friction time constant a flick is projected through: a release at `v`
 *  travels `v · FLICK_TAU`, and its reciprocal is the rate that travel decays
 *  at. Chosen from REACH rather than from a decay rate — a brisk flick is
 *  around 2000px/s, and 0.15s carries it 300px, which on the 44px cell this
 *  component renders is nearly seven values; a hard one at 6000px/s crosses
 *  twenty. iOS spells the same constant as a per-millisecond decay rate, and
 *  0.15s sits between its 0.1s "fast" (a picker) and 0.5s "normal" (a scroll
 *  view): these wheels are 24 and 60 values long, so a picker's own rate
 *  barely crosses them, and a scroll view's overshoots the whole column. */
export const FLICK_TAU = 0.15

/** The rate a move with nothing thrown behind it runs at, rad/s — a tap on a
 *  neighbour, an arrow key, a park, a band returning under a finger that had
 *  already stopped. 18 rad/s brings one 44px cell to a stop in under four
 *  tenths of a second, which is where a one-step correction belongs. It is
 *  deliberately NOT the floor of a thrown move: a throw decays at its own
 *  friction, and forcing it up to this rate is what turns a coast into a
 *  lunge at the target. */
export const OMEGA_STILL = 18

/** The ceiling. `settleOmega` matches the rate to the gesture, which grows
 *  without bound as the remaining distance goes to zero — a hard flick whose
 *  projection the slot rule pulls back to a detent a few pixels away. Past
 *  60 rad/s (a 17ms time constant) the difference is a jump, and a jump is
 *  what it should be. */
export const OMEGA_MAX = 60

/** iOS's own rubber-band constant. `f'(0) = c`, so the first pixel past the
 *  end moves just over half a pixel and the resistance grows from there. */
export const RUBBER_C = 0.55

/** Sub-pixel: below this the wheel is where it is going. */
export const AT_REST = 0.5

/** …and slow enough that it is not merely passing through — a third of a pixel
 *  per frame at 60Hz. Both gates, so a wheel travelling fast through its target
 *  is never mistaken for one that stopped on it. */
export const AT_REST_SPEED = 20

/**
 * The wheel's speed at release, px/s, as the least-squares slope of the samples
 * inside the trailing window.
 *
 * A slope over a dozen readings rather than a difference between the last two:
 * at 120Hz consecutive samples are 8ms apart, so one pixel of pointer noise
 * reads as 125px/s of velocity and a flick's projection inherits it. The fit
 * costs one pass and is immune to that. Fewer than two readings, or a window
 * with no time in it, is not a measurement — that is 0, not an extrapolation.
 */
export function velocityFrom(samples: readonly Sample[], now: number, window: number = VELOCITY_WINDOW): number {
	const recent = samples.filter(s => now - s.at <= window)
	if (recent.length < 2) return 0
	const meanAt = recent.reduce((sum, s) => sum + s.at, 0) / recent.length
	const meanY = recent.reduce((sum, s) => sum + s.y, 0) / recent.length
	const spread = recent.reduce((sum, s) => sum + (s.at - meanAt) ** 2, 0)
	if (spread === 0) return 0
	return recent.reduce((sum, s) => sum + (s.at - meanAt) * (s.y - meanY), 0) / spread
}

/**
 * Where a release at `velocity` comes to rest, computed rather than simulated.
 *
 * Exponential friction has a closed form — the whole remaining travel is
 * `v · τ` — so the destination is known in the frame the finger lifts, which is
 * what lets the detent be chosen BEFORE the motion starts. Deciding it at the
 * end instead means running a decay, watching where it stopped and then pulling
 * the wheel off that spot, which is the flick fighting the user rather than
 * landing where they aimed.
 */
export function project(offset: number, velocity: number, tau: number = FLICK_TAU): number {
	return offset + velocity * tau
}

/**
 * How far the wheel actually moves when it is dragged `over` pixels past its
 * end — iOS's curve, `f(d) = (1 − 1/(d·c/D + 1))·D`.
 *
 * Resistance grows with distance and the travel is asymptotic to `D`, so the
 * end can be pulled away from but never off: the first pixel yields `c` of a
 * pixel, the hundredth a small fraction of one, and no drag however long
 * reaches `D`. `D` is the wheel's own visible height, so the give is
 * proportional to the surface being pulled rather than a constant somebody
 * picked.
 */
export function rubberBand(over: number, dimension: number, c: number = RUBBER_C): number {
	if (over === 0 || dimension <= 0) return 0
	const distance = Math.abs(over)
	return Math.sign(over) * (1 - 1 / ((distance * c) / dimension + 1)) * dimension
}

/**
 * A raw drag offset mapped onto the offset the wheel is actually placed at:
 * itself inside the ends, resisted beyond them.
 */
export function rubberBanded(
	raw: number,
	lower: number,
	upper: number,
	dimension: number,
	c: number = RUBBER_C,
): number {
	if (raw > upper) return upper + rubberBand(raw - upper, dimension, c)
	if (raw < lower) return lower + rubberBand(raw - lower, dimension, c)
	return raw
}

/**
 * The detent a projection lands on: the nearest one, with a tie broken along
 * the direction of travel so a flick never resolves backwards.
 *
 * `detents` carries only the offsets a wheel may REST on, so a projection that
 * falls on a dead value continues to the nearest live neighbour with no special
 * case — and a sparse set (a window admitting one value in an hour) is the same
 * arithmetic, which is what makes the slot rule binding on the physics rather
 * than layered over it. `null` only when nothing is selectable at all.
 */
export function nearestDetent(detents: readonly number[], wanted: number, direction: number = 0): number | null {
	if (!detents.length) return null
	return detents.reduce((best, detent) => {
		const gap = Math.abs(detent - wanted)
		const bestGap = Math.abs(best - wanted)
		if (gap < bestGap) return detent
		if (gap === bestGap && direction !== 0 && Math.sign(detent - wanted) === Math.sign(direction)) return detent
		return best
	}, detents[0])
}

/**
 * The rate the settle runs at: the one that makes the release velocity exactly
 * the speed the spring's own decay already carries at that displacement.
 *
 * A critically damped spring from `x₀` with velocity `v₀` reduces to a pure
 * exponential — and so cannot overshoot, and so IS the momentum decay rather
 * than a second motion layered over it — precisely when `ω = |v₀| / |x₀|`. So
 * the rate is not a constant: it is read off the gesture. A release the slot
 * rule did not have to correct gives back `1 / FLICK_TAU` exactly, which is the
 * friction the projection was computed with; a flick the slot rule pulled up
 * short gives back a faster rate, because it sheds the same speed over a
 * shorter distance; and a move nobody threw gives back `OMEGA_STILL`, because
 * there is no velocity to match.
 *
 * Between those two lies every release whose snap LENGTHENED the travel, where
 * part of the distance is momentum and the rest is correction. `thrown` is that
 * proportion — `|v|·τ / |x|`, 1 when the whole distance is the flick's own — and
 * the rate is read off it, so a barely-thrown move corrects at nearly the still
 * rate and a fully-thrown one coasts at exactly the friction. Taking the faster
 * of the two flat rates instead would make a gentle release with a half-cell
 * correction take most of a second to cover forty pixels, because `1 / τ` is a
 * coast rate and forty pixels is not a coast.
 *
 * The function is continuous across all three: at `thrown = 1` the blend and
 * the matched rate both give `1 / τ`, and at `thrown = 0` the blend gives the
 * still rate the un-thrown branch returns.
 *
 * Velocity pointing AWAY from the target is not a rate — the wheel has to turn
 * around first — so it takes the still rate and `springAt`'s ramp term does the
 * turning.
 */
export function settleOmega(fromTarget: number, velocity: number): number {
	if (fromTarget === 0 || Math.sign(velocity) !== -Math.sign(fromTarget)) return OMEGA_STILL
	const thrown = (Math.abs(velocity) * FLICK_TAU) / Math.abs(fromTarget)
	return thrown >= 1 ? Math.min(OMEGA_MAX, thrown / FLICK_TAU) : OMEGA_STILL + thrown * (1 / FLICK_TAU - OMEGA_STILL)
}

/**
 * The velocity the settle spring is seeded with: the measured one, reduced only
 * as far as monotonicity requires.
 *
 * A critically damped spring at displacement `x` with velocity `v` crosses its
 * target exactly when the approach is faster than `ω·|x|` — the speed the
 * spring's own decay would carry there — so clamping to that bound makes the
 * landing provably overshoot-free. Paired with `settleOmega` the clamp is
 * normally a no-op, because that rate already IS `|v| / |x|`; it bites only
 * where the rate was capped at `OMEGA_MAX`, which is the one case a matched
 * rate cannot express and the one case a jump is the right answer anyway.
 */
export function seedVelocity(fromTarget: number, velocity: number, omega: number): number {
	const terminal = -omega * fromTarget
	return fromTarget >= 0 ? Math.max(velocity, terminal) : Math.min(velocity, terminal)
}

/**
 * The critically damped spring, evaluated in closed form at `elapsed` seconds
 * after release: `x(t) = (x₀ + (v₀ + ω·x₀)·t)·e^{−ωt}`.
 *
 * Evaluated from the release rather than integrated frame by frame, so the
 * trajectory is identical at 60Hz, at 120Hz and across a frame the browser
 * dropped — an integrator's is not, and a picker that lands differently on a
 * ProMotion display than on an external monitor is a picker with two feels.
 *
 * Both halves are returned relative to the target: `offset` is the remaining
 * displacement, `velocity` its derivative.
 */
export function springAt(
	fromTarget: number,
	velocity: number,
	elapsed: number,
	omega: number,
): { offset: number; velocity: number } {
	const decay = Math.exp(-omega * elapsed)
	const ramp = velocity + omega * fromTarget
	const travelled = fromTarget + ramp * elapsed
	return { offset: travelled * decay, velocity: (ramp - omega * travelled) * decay }
}

/** Whether a spring has arrived — near enough AND slow enough, so a wheel still
 *  travelling through its target is not mistaken for one that stopped on it. */
export function atRest(fromTarget: number, velocity: number): boolean {
	return Math.abs(fromTarget) < AT_REST && Math.abs(velocity) < AT_REST_SPEED
}
